import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport surface for the admin questionnaire-templates feature + the stubbed
 * delivery-methods lookup. The generated `schema.ts` now carries the contract types
 * (`QuestionnaireTemplate`, `DocumentDeliveryMethod`); these slice DTOs are kept
 * hand-written on purpose — `QuestionnaireTemplateDto` is all-optional (mirrors
 * `IdentifierDefinitionDto`) so the same type serves both the create body and the
 * response without lint friction.
 */

/**
 * Wire DTO for a questionnaire template (admin catalog entry). All fields optional on the
 * wire (mirrors IdentifierDefinitionDto); the view-model mapper normalises to non-null.
 */
export interface QuestionnaireTemplateDto {
  id?: string;
  template_key?: string;
  label?: string;
  active?: boolean;
  email_subject?: string | null;
  email_body_html?: string | null;
  email_body_text?: string | null;
  email_server_id?: string | null;
}

/** Spring `Page<QuestionnaireTemplate>` envelope. */
export type QuestionnaireTemplatePageDto = PageEnvelope<QuestionnaireTemplateDto>;

export type DocumentDeliveryMethodId = 'EMAIL' | 'SMS' | 'URL';

/** Available delivery channel (stubbed in Phase 1). `manual === true` for URL. */
export interface DocumentDeliveryMethodDto {
  id: DocumentDeliveryMethodId;
  label: string;
  manual: boolean;
}

/** Whether sending questionnaires is configured (an effective PFQ application URL is resolvable). */
export interface QuestionnaireConfigDto {
  configured: boolean;
}

/** Admin "Generate" result: a PFQ URL discovered from the registry, or null when not registered. */
export interface PpqSuggestedUrlDto {
  url: string | null;
}

export const adminQuestionnaireTemplatesEndpoints = {
  /** GET /questionnaire-templates — paginated catalog for the current tenant. */
  getQuestionnaireTemplates: (
    params?: { page?: number; size?: number; active?: boolean; signal?: AbortSignal },
  ): Promise<QuestionnaireTemplatePageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.active !== undefined) search.set('active', String(params.active));
    const query = search.toString();
    const path = query.length > 0 ? `/questionnaire-templates?${query}` : '/questionnaire-templates';
    return http.get<QuestionnaireTemplatePageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /** POST /questionnaire-templates — create a tenant-scoped template. 409 if key/label taken. */
  createQuestionnaireTemplate: (
    body: QuestionnaireTemplateDto,
    options?: { signal?: AbortSignal },
  ): Promise<QuestionnaireTemplateDto> =>
    http.post<QuestionnaireTemplateDto>('/questionnaire-templates', body, options),

  /** PUT /questionnaire-templates/{id} — update. 409 if the new key/label collides. */
  updateQuestionnaireTemplate: (
    id: string,
    body: QuestionnaireTemplateDto,
    options?: { signal?: AbortSignal },
  ): Promise<QuestionnaireTemplateDto> =>
    http.put<QuestionnaireTemplateDto>(
      `/questionnaire-templates/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /** DELETE /questionnaire-templates/{id}. 204; 409 (template_in_use) when referenced by a request. */
  deleteQuestionnaireTemplate: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/questionnaire-templates/${encodeURIComponent(id)}`, options),

  /** GET /questionnaires/delivery-methods — stubbed list of delivery channels. */
  getQuestionnaireDeliveryMethods: (options?: {
    signal?: AbortSignal;
  }): Promise<DocumentDeliveryMethodDto[]> =>
    http.get<DocumentDeliveryMethodDto[]>('/questionnaires/delivery-methods', options),

  /** GET /questionnaires/config — whether sending is configured for the current tenant. */
  getQuestionnaireConfig: (options?: { signal?: AbortSignal }): Promise<QuestionnaireConfigDto> =>
    http.get<QuestionnaireConfigDto>('/questionnaires/config', options),

  /** GET /questionnaires/config/suggested-url — admin: discover the PFQ URL from the registry. */
  getQuestionnaireSuggestedUrl: (options?: { signal?: AbortSignal }): Promise<PpqSuggestedUrlDto> =>
    http.get<PpqSuggestedUrlDto>('/questionnaires/config/suggested-url', options),
};
